import React, { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';

import { BaseActionButtonProps } from '../BaseActionButtonProps';

import commonStyles from '../ActionButtonCommon.module.scss';
import styles from './KeepScreenOnToggle.module.scss';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import useDictionary from 'lib/hooks/useDictionary';
import { useWakeLock } from './WakeLockContext';
import { pushToDataLayer } from 'components/helpers/Global/Analytics/DataLayer';

// Module-level flag to ensure GTM event is only sent once across all instances
let gtmEventSent = false;

interface KeepScreenOnToggleProps extends BaseActionButtonProps {
  isSubAction?: boolean;
}

export function KeepScreenOnToggle({
  label,
  variant,
  isSubAction = false,
  toolbarIdentifier,
  eventPositionSuffix,
}: KeepScreenOnToggleProps): JSX.Element {
  const { isToggled, wakeLockSupported, toggle } = useWakeLock();
  const { getDictionaryValue } = useDictionary();

  const handleClick = () => {
    toggle();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  const getGtmPosition = useCallback((): string => {
    return [toolbarIdentifier, eventPositionSuffix].filter(Boolean).join('.') || '';
  }, [toolbarIdentifier, eventPositionSuffix]);

  const gtmEvent = {
    event: 'wake_lock_toggle_enabled',
    position: getGtmPosition(),
  };

  const gtmEventOnLoad = useMemo(
    () => ({
      event: 'wake_lock_toggle_available',
      position: getGtmPosition(),
    }),
    [getGtmPosition]
  );

  useEffect(() => {
    if (wakeLockSupported && !gtmEventSent) {
      pushToDataLayer({ ...gtmEventOnLoad });
      gtmEventSent = true;
    }
  }, [wakeLockSupported, gtmEventOnLoad]);

  return (
    <div
      className={clsx({
        [commonStyles['actionButtonWrapper--horizontal']]: variant === 'horizontal',
      })}
    >
      <CtaButton
        ctaStyle={variant === 'horizontal' && !isSubAction ? 'link' : 'none'}
        ctaVariant="brand"
        type="button"
        className={clsx(
          commonStyles.actionButtonBase,
          commonStyles[`actionButton--${variant}`],
          styles.keepToggleOnButton,
          styles[variant],
          {
            [styles.checked]: isToggled,
            [styles.disabled]: !wakeLockSupported,
          },
          isSubAction && commonStyles['actionButtonSubAction']
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="switch"
        aria-checked={isToggled}
        aria-label={
          wakeLockSupported
            ? label
            : `${label} ${getDictionaryValue('Components.ActionToolBar.WakeLockNotSupported') || 'Wake Lock not supported'}`
        }
        tabIndex={!wakeLockSupported ? -1 : 0}
        gtmEvent={!isToggled ? gtmEvent : undefined}
        ctaIconBefore={isToggled ? 'toggleOnSwitch' : 'toggleOffSwitch'}
        ctaIconClassName={clsx(styles.toggleIcon, { [styles.checked]: isToggled })}
        ctaIconSize="sm"
        ctaIconNoSpacer={variant === 'vertical'}
        text={label}
      />
    </div>
  );
}
